import { useMemo, useState } from 'react';
import { Button, Empty, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  BookOutlined,
  CaretDownOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useAppDispatch, useAppState } from '../store/hooks';
import { track } from '../utils/track';
import { useContractTemplates } from '../contract/useContractTemplates';
import { useTemplates } from '../store/useTemplates';
import { summarizeContractTemplate, summarizeTableTemplate } from './util';
import type { Template } from '../api/types';
import type { ContractTemplate } from '../contract/types';
import { MAX_CONTRACT_TEMPLATES_PER_TABLE, MAX_TEMPLATES_PER_TABLE } from '../utils/constants';

interface RowProps {
  name: string;
  summary: string;
  selected: boolean;
  icon: React.ReactNode;
  onSelect: () => void;
  onDelete: () => void;
}

function TemplateRow({ name, summary, selected, icon, onSelect, onDelete }: RowProps) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div
      style={{
        background: 'var(--surface-1)',
        border: '1px solid ' + (selected ? 'var(--ink)' : 'var(--border)'),
        borderRadius: 10,
        boxShadow: selected ? '0 0 0 3px var(--accent-soft)' : '0 1px 2px rgba(0,0,0,.02)',
        overflow: 'hidden',
        transition: 'border-color .12s, box-shadow .12s',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <button
          className="pp-bare-button"
          onClick={onSelect}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: selected ? 'var(--accent)' : 'var(--surface-2)',
              color: selected ? 'var(--accent-ink)' : 'var(--ink-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--ink)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {name}
              </span>
              {selected && (
                <span
                  style={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: 'var(--accent)',
                    padding: '1px 5px',
                    background: 'var(--accent-soft)',
                    borderRadius: 3,
                    flexShrink: 0,
                    letterSpacing: '.02em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <StarFilled style={{ fontSize: 9 }} /> 当前
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--ink-3)',
                marginTop: 2,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '-.02em',
              }}
            >
              {summary}
            </div>
          </div>
          <RightOutlined style={{ color: 'var(--ink-4)', fontSize: 11 }} />
        </button>
        <Tooltip title="删除">
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setConfirm(true);
            }}
          />
        </Tooltip>
      </div>
      {confirm && (
        <div
          style={{
            padding: '8px 12px 10px',
            borderTop: '1px solid var(--border-soft)',
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 11.5, color: 'var(--ink-2)', flex: 1 }}>
            删除「{name}」？此操作无法撤销。
          </span>
          <Button size="small" type="text" onClick={() => setConfirm(false)}>
            取消
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setConfirm(false);
              onDelete();
            }}
          >
            删除
          </Button>
        </div>
      )}
    </div>
  );
}

function HowItWorks({ mode }: { mode: 'table' | 'contract' }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        marginBottom: 10,
        background: 'var(--accent-soft)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <button
        className="pp-bare-button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: 'var(--accent)' }}>✦</span>
        <span style={{ fontSize: 11.5, color: 'var(--ink)', fontWeight: 500, flex: 1 }}>
          模板怎么工作？
        </span>
        <span
          style={{
            color: 'var(--ink-3)',
            display: 'flex',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform .15s',
          }}
        >
          <CaretDownOutlined style={{ fontSize: 11 }} />
        </span>
      </button>
      {open && (
        <div
          style={{
            padding: '0 12px 10px 12px',
            fontSize: 11.5,
            color: 'var(--ink-2)',
            lineHeight: 1.7,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr', gap: '4px 8px' }}>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>1.</span>
            <span>
              点任意模板进入编辑器，
              {mode === 'contract' ? (
                <>调整<strong>区块顺序 / 显隐 / 排版</strong></>
              ) : (
                <>调整<strong>字段勾选 / 排序 / 排版</strong></>
              )}
              ，自动保存。
            </span>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>2.</span>
            <span>
              点「+ 新建」创建空白模板 → 进编辑器配置。
            </span>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>3.</span>
            <span>
              回主页时所选模板的配置会用来生成预览和打印。
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function TemplatesPage() {
  const state = useAppState();
  const dispatch = useAppDispatch();
  const contractTpl = useContractTemplates(state.context?.tableId ?? null);
  const tableTpl = useTemplates(state.context?.tableId ?? null);
  const isContract = state.mode === 'contract';

  const sortedContract: ContractTemplate[] = useMemo(
    () => [...contractTpl.templates].sort((a, b) => b.updatedAt - a.updatedAt),
    [contractTpl.templates],
  );
  const sortedTable: Template[] = useMemo(
    () => [...tableTpl.templates].sort((a, b) => b.updatedAt - a.updatedAt),
    [tableTpl.templates],
  );

  const list = isContract ? sortedContract : sortedTable;
  const selectedId = isContract ? contractTpl.selectedId : tableTpl.selectedId;

  const onBack = () => dispatch({ type: 'NAV', view: 'home' });

  const onNew = () => {
    track('tpl_create');
    if (isContract) {
      const t = contractTpl.create('未命名模板');
      if (t) dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    } else {
      const t = tableTpl.create(
        '未命名模板',
        state.fieldOptions.filter((f) => f.checked).map((f) => f.id),
        state.settings,
      );
      if (t) dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    }
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface-1)',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Button shape="circle" type="text" icon={<LeftOutlined />} onClick={onBack} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>
            {isContract ? (sortedTable.length > 0 ? '记录模板' : '模板') : '视图模板'}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              fontFamily: 'var(--font-mono)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {state.context ? `${state.context.tableId.slice(0, 12)}` : '未连接'} · 本地保存 ·{' '}
            {list.length}/{isContract ? MAX_CONTRACT_TEMPLATES_PER_TABLE : MAX_TEMPLATES_PER_TABLE}
          </div>
        </div>
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={onNew}
        >
          新建
        </Button>
      </div>

      <div
        style={{ flex: 1, overflowY: 'auto', padding: 12, minHeight: 0 }}
        className="pp-scroll"
      >
        <HowItWorks mode={isContract ? 'contract' : 'table'} />

        {list.length === 0 && (
          <Empty
            description={
              <span style={{ color: 'var(--ink-3)' }}>
                还没有模板，点右上「新建」开始
              </span>
            }
          />
        )}

        <div style={{ display: 'grid', gap: 8 }}>
          {isContract
            ? sortedContract.map((t) => (
                <TemplateRow
                  key={t.id}
                  name={t.name}
                  summary={(t.shared ? '团队共享 · ' : '') + summarizeContractTemplate(t)}
                  selected={t.id === selectedId}
                  icon={<BookOutlined />}
                  onSelect={() => {
                    contractTpl.setSelected(t.id);
                    dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
                  }}
                  onDelete={() => contractTpl.remove(t.id)}
                />
              ))
            : sortedTable.map((t) => (
                <TemplateRow
                  key={t.id}
                  name={t.name}
                  summary={summarizeTableTemplate(t)}
                  selected={t.id === selectedId}
                  icon={<AppstoreOutlined />}
                  onSelect={() => {
                    tableTpl.setSelected(t.id);
                    dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
                  }}
                  onDelete={() => tableTpl.remove(t.id)}
                />
              ))}
        </div>

        <div
          style={{
            marginTop: 16,
            padding: '10px 12px',
            fontSize: 11.5,
            color: 'var(--ink-3)',
            background: 'var(--surface-2)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <CloudSyncOutlined style={{ flexShrink: 0, marginTop: 2 }} />
          <span>模板与偏好保存在本地浏览器，按 tableId 隔离。切换设备不会同步。</span>
        </div>
      </div>
    </div>
  );
}
